/**
 * CurrencyContext — manages CRC / USD display toggle for financial reports.
 *
 * When multimoneda is enabled the user can switch between showing amounts in
 * colones (CRC) or dollars (USD).  The amounts for each currency come directly
 * from their respective backend fields (debito/credito vs debito_usd/credito_usd)
 * — NO division by TC is performed here.
 *
 * Exported:
 *   CurrencyProvider  — wrap the financial-reports subtree with this
 *   useCurrency()     — hook that returns:
 *                         { isUSD, setIsUSD, multimoneda, setMultimoneda,
 *                           tc, symbol, fmtCRC, fmtCRCSigned }
 */
import { createContext, useContext, useState, useEffect } from "react";
import apiFetch from "../services/apiClient";

const CurrencyCtx = createContext(null);

// ── Pure number formatter (no symbol) ────────────────────────────────────────
function numFmt(v, locale) {
  return Math.abs(Number(v ?? 0)).toLocaleString(locale, { minimumFractionDigits: 2 });
}

// ── Build helpers for a given (isUSD) pair ────────────────────────────────────
// NOTE: amounts are already in the target currency — no TC conversion needed.
function buildHelpers(isUSD) {
  const symbol = isUSD ? "$" : "₡";
  const locale = isUSD ? "en-US" : "es-CR";

  /** Format an unsigned amount in the current display currency */
  function fmtCRC(n) {
    return `${symbol}${numFmt(n, locale)}`;
  }

  /** Format a signed amount (shows leading "−" when negative) */
  function fmtCRCSigned(n) {
    const v   = Number(n ?? 0);
    const neg = v < -0.005;
    return `${neg ? "-" : ""}${symbol}${numFmt(Math.abs(v), locale)}`;
  }

  return { symbol, fmtCRC, fmtCRCSigned };
}

// ── Provider ──────────────────────────────────────────────────────────────────
export function CurrencyProvider({ children }) {
  const [isUSD,       setIsUSD]       = useState(false);
  const [multimoneda, setMultimoneda] = useState(false);
  // TC is fetched for informational display only (not used for conversion)
  const [tc,          setTc]          = useState(1);

  useEffect(() => {
    apiFetch("/api/finance/tipo-cambio/hoy/")
      .then(r => r.json())
      .then(d => setTc(Number(d.tipo_compra) || 1))
      .catch(() => {});
  }, []);

  // Reset to CRC when multimoneda is disabled
  useEffect(() => {
    if (!multimoneda) setIsUSD(false);
  }, [multimoneda]);

  const helpers = buildHelpers(isUSD);

  return (
    <CurrencyCtx.Provider value={{
      tc, isUSD, setIsUSD,
      multimoneda, setMultimoneda,
      ...helpers,
    }}>
      {children}
    </CurrencyCtx.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────
const FALLBACK = { tc: 1, isUSD: false, setIsUSD: () => {}, multimoneda: false,
  setMultimoneda: () => {}, ...buildHelpers(false) };

export function useCurrency() {
  return useContext(CurrencyCtx) ?? FALLBACK;
}
